import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AlertCircle, ChevronDown, ChevronUp, Check } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

export default function DisputeReasonSection({ 
  selectedCardholder,
  selectedCard,
  disputeReason,
  onDisputeReasonChange,
  disabled
}) {
  const [expanded, setExpanded] = useState(true);
  const [category, setCategory] = useState(disputeReason?.category || "");
  const [type, setType] = useState(disputeReason?.type || "");

  useEffect(() => {
    if (disputeReason) {
      setCategory(disputeReason.category);
      setType(disputeReason.type);
      setExpanded(false);
    } else {
      setCategory("");
      setType("");
    }
  }, [disputeReason]);

  // Reset when cardholder or card changes
  useEffect(() => {
    if (!selectedCardholder || !selectedCard) {
      setCategory("");
      setType("");
    }
  }, [selectedCardholder, selectedCard]);

  const handleCategoryChange = (value) => {
    setCategory(value);
    setType("");
  };

  const handleTypeChange = (value) => {
    setType(value);
    
    // Apply the selections
    onDisputeReasonChange({ 
      category: category,
      type: value 
    });
    
    // Collapse this section
    setExpanded(false);
  };

  const clearDisputeReason = () => {
    setCategory("");
    setType("");
    onDisputeReasonChange(null);
    setExpanded(true);
  };

  return (
    <Card className="mb-6 border-l-4 border-l-red-500 shadow-md">
      <CardHeader className="flex flex-row items-center justify-between py-4">
        <CardTitle 
          className={`text-lg font-semibold flex items-center ${!disabled ? 'cursor-pointer' : ''}`}
          onClick={() => !disabled && setExpanded(!expanded)}
        >
          <AlertCircle className="mr-2 h-5 w-5 text-red-500" />
          Dispute Reason
          {expanded ? 
            <ChevronUp className="ml-2 h-4 w-4" /> : 
            <ChevronDown className="ml-2 h-4 w-4" />
          }
        </CardTitle>
        {disputeReason && (
          <div className="flex items-center">
            <span className="text-sm bg-green-100 text-green-800 py-1 px-3 rounded-full flex items-center">
              <Check className="mr-1 h-3 w-3" />
              Dispute Reason Selected
            </span>
          </div>
        )}
      </CardHeader>
      
      <AnimatePresence>
        {expanded && !disputeReason && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <CardContent className="space-y-6 pb-6">
              {!disabled ? (
                <>
                  <div>
                    <h3 className="text-base font-medium mb-3">Is this a Fraud or Non-Fraud dispute?</h3>
                    <div className="flex flex-wrap gap-3">
                      <Button
                        type="button"
                        variant={category === "Fraud" ? "default" : "outline"}
                        onClick={() => handleCategoryChange("Fraud")}
                        className={category === "Fraud" ? "bg-red-600" : ""}
                      >
                        Fraud
                      </Button>
                      <Button
                        type="button"
                        variant={category === "Non-Fraud" ? "default" : "outline"}
                        onClick={() => handleCategoryChange("Non-Fraud")}
                        className={category === "Non-Fraud" ? "bg-red-600" : ""}
                      >
                        Non-Fraud
                      </Button>
                    </div>
                  </div>
                  
                  {category === "Fraud" && (
                    <div>
                      <h3 className="text-base font-medium mb-3">What type of Fraud?</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <Button
                          type="button"
                          variant={type === "Unauthorized transaction – Card in possession" ? "default" : "outline"}
                          onClick={() => handleTypeChange("Unauthorized transaction – Card in possession")}
                          className={`justify-start text-left ${type === "Unauthorized transaction – Card in possession" ? "bg-red-600" : ""}`}
                        >
                          Unauthorized transaction – Card in possession
                        </Button>
                        <Button
                          type="button"
                          variant={type === "Lost card" ? "default" : "outline"}
                          onClick={() => handleTypeChange("Lost card")}
                          className={`justify-start text-left ${type === "Lost card" ? "bg-red-600" : ""}`}
                        >
                          Lost card
                        </Button>
                        <Button
                          type="button"
                          variant={type === "Stolen card" ? "default" : "outline"}
                          onClick={() => handleTypeChange("Stolen card")}
                          className={`justify-start text-left ${type === "Stolen card" ? "bg-red-600" : ""}`}
                        >
                          Stolen card
                        </Button>
                        <Button
                          type="button"
                          variant={type === "Card not received" ? "default" : "outline"}
                          onClick={() => handleTypeChange("Card not received")}
                          className={`justify-start text-left ${type === "Card not received" ? "bg-red-600" : ""}`}
                        >
                          Card not received
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  {category === "Non-Fraud" && (
                    <div>
                      <h3 className="text-base font-medium mb-3">What type of Non-Fraud dispute?</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <Button
                          type="button"
                          variant={type === "Merchandise not received" ? "default" : "outline"}
                          onClick={() => handleTypeChange("Merchandise not received")}
                          className={`justify-start text-left ${type === "Merchandise not received" ? "bg-red-600" : ""}`}
                        >
                          Merchandise not received
                        </Button>
                        <Button
                          type="button"
                          variant={type === "Incorrect amount billed" ? "default" : "outline"}
                          onClick={() => handleTypeChange("Incorrect amount billed")}
                          className={`justify-start text-left ${type === "Incorrect amount billed" ? "bg-red-600" : ""}`}
                        >
                          Incorrect amount billed
                        </Button>
                        <Button
                          type="button"
                          variant={type === "Duplicate transaction" ? "default" : "outline"}
                          onClick={() => handleTypeChange("Duplicate transaction")}
                          className={`justify-start text-left ${type === "Duplicate transaction" ? "bg-red-600" : ""}`}
                        >
                          Duplicate transaction
                        </Button>
                        <Button
                          type="button"
                          variant={type === "Dissatisfaction with product or service" ? "default" : "outline"}
                          onClick={() => handleTypeChange("Dissatisfaction with product or service")}
                          className={`justify-start text-left ${type === "Dissatisfaction with product or service" ? "bg-red-600" : ""}`}
                        >
                          Dissatisfaction with product or service
                        </Button>
                        <Button
                          type="button"
                          variant={type === "Cancelled recurring transaction" ? "default" : "outline"}
                          onClick={() => handleTypeChange("Cancelled recurring transaction")}
                          className={`justify-start text-left ${type === "Cancelled recurring transaction" ? "bg-red-600" : ""}`}
                        >
                          Cancelled recurring transaction
                        </Button>
                        <Button
                          type="button"
                          variant={type === "Merchant credit not issued" ? "default" : "outline"}
                          onClick={() => handleTypeChange("Merchant credit not issued")}
                          className={`justify-start text-left ${type === "Merchant credit not issued" ? "bg-red-600" : ""}`}
                        >
                          Merchant credit not issued
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center p-4 text-gray-500">
                  Please select a cardholder and card first
                </div>
              )}
            </CardContent>
          </motion.div>
        )}
        
        {!expanded && disputeReason && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <CardContent className="py-2">
              <div className="flex justify-between items-center">
                <div className="grid grid-cols-2 gap-4 flex-grow">
                  <div>
                    <div className="text-sm text-gray-500">Dispute Category</div>
                    <div className="font-medium">{disputeReason.category}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">Dispute Type</div>
                    <div className="font-medium">{disputeReason.type}</div>
                  </div>
                </div>
                {!disabled && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={clearDisputeReason}
                    className="text-gray-400 hover:text-gray-600 ml-2"
                  >
                    Change
                  </Button>
                )}
              </div>
            </CardContent>
          </motion.div>
        )}
      </AnimatePresence>
    </Card>
  );
}